"use client";

import { useState, useMemo } from "react";
import { Button, Card } from "@repo/ui";
import { getNextCohort } from "@repo/constants/between-sessions";
import {
  Users,
  Clock,
  TrendingUp,
  Heart,
  Laptop,
  CheckCircle,
  Star,
  ArrowRight,
  Sparkles,
  GraduationCap,
  MessageCircle,
  Target,
  Zap,
  Shield,
  ChevronDown,
  ChevronUp,
  Brain,
  Briefcase,
  BookOpen,
  Award,
  Coffee,
  Headphones,
  MapPin,
  DollarSign,
  FileText,
  Calendar,
  Lightbulb,
  HandHeart,
  Rocket,
  HelpCircle,
  AlertCircle,
  Percent
} from "lucide-react";
import PathQuizModal from "./PathQuizModal";

export default function PracticumPage() {
  const airtableFormUrl = "https://airtable.com/appWmJaYnVtadhoXi/pagFpY7hOqBXaAvSM/form";
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [hoveredBenefit, setHoveredBenefit] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'students' | 'supervisors'>('students');
  const [activeLab, setActiveLab] = useState(0);
  const [isQuizOpen, setIsQuizOpen] = useState(false);

  // Get next cohort intake date
  const nextCohort = useMemo(() => getNextCohort(), []);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const impactStats = [
    { number: "10,000+", label: "clinical hours delivered", icon: Clock, color: "from-blue-100 to-blue-200" },
    { number: "1000+", label: "interns graduated using Alli", icon: GraduationCap, color: "from-purple-100 to-purple-200" },
    { number: "100%", label: "Virtual placement for your busy life", icon: Laptop, color: "from-green-100 to-green-200" }
  ];

  const programHighlights = [
    {
      icon: Users,
      title: "Real Clients, Real Impact",
      description: "Work with 15-25 diverse clients experiencing genuine transformation",
      stat: "20+ clients",
      gradient: "from-blue-50 to-blue-100"
    },
    {
      icon: Brain,
      title: "Expert Supervision",
      description: "Weekly 90-minute dyad supervision and monthly group supervision from experienced supervisors",
      stat: "Weekly support",
      gradient: "from-purple-50 to-purple-100"
    },
    {
      icon: Laptop,
      title: "Work From Anywhere",
      description: "Complete your practicum from home or any private, quiet space",
      stat: "100% remote",
      gradient: "from-green-50 to-green-100"
    },
    {
      icon: Rocket,
      title: "Career Development",
      description: "Build your professional network and clinical confidence",
      stat: "Future ready",
      gradient: "from-orange-50 to-orange-100"
    },
    {
      icon: Coffee,
      title: "Flexible Schedule",
      description: "See clients on your schedule - mornings, evenings, or weekends",
      stat: "You choose",
      gradient: "from-indigo-50 to-indigo-100"
    },
    {
      icon: Award,
      title: "Additional Training",
      description: "Access specialized workshops, clinical trainings, and professional development resources",
      stat: "Ongoing learning",
      gradient: "from-yellow-50 to-yellow-100"
    }
  ];

  const journeySteps = [
    {
      phase: "1 Month Before",
      title: "Preparation Phase",
      items: ["Complete online training modules", "Build your professional profile", "Complete documentation", "Set yourself up for success"],
      icon: BookOpen,
      color: "bg-blue-100"
    },
    {
      phase: "Week 1",
      title: "Welcome & Connect",
      items: ["Interactive live training", "Meet your clinical supervisor", "Bond with your cohort", "Get matched with first clients"],
      icon: Users,
      color: "bg-purple-100"
    },
    {
      phase: "Weeks 2-4",
      title: "Launch Your Practice",
      items: ["Begin client sessions", "Apply evidence-based techniques", "Receive weekly supervision", "Build clinical confidence"],
      icon: TrendingUp,
      color: "bg-green-100"
    },
    {
      phase: "Month 4+",
      title: "Advanced Practice",
      items: ["Specialize in your passion areas", "Take on complex cases", "Welcome new students", "Pursue continued education"],
      icon: Star,
      color: "bg-indigo-100"
    },
    {
      phase: "Month 8",
      title: "Career Ready",
      items: ["Graduate with 200+ hours", "Celebrate your achievements", "Access job opportunities", "Stay connected as alumni"],
      icon: Award,
      color: "bg-orange-100"
    }
  ];

  const testimonials = [
    {
      name: "Kelly Kozluk",
      program: "Clinical Psychology Student",
      quote: "My practicum experience at Alli has exceeded my expectations in all areas. Not only have I been able to get my hours with internal supervision, but I have been welcomed into the team with open arms, been provided with countless opportunities to grow personally and professionally and have control over my experience.",
      image: "KK",
      highlight: "Exceeded all expectations",
      gradient: "from-purple-400 to-pink-400"
    },
    {
      name: "Asha Katz-Feinstein",
      program: "Counselling Psychology Student",
      quote: "My practicum with Alli has been more than I could have hoped for. The team are all deeply supportive of my learning process. Not only have I been able to hone my skills as a therapist with a full client load, I have been able to participate in a number of projects that have prepared me for the work I will do as a therapist outside of sessions.",
      image: "AK",
      highlight: "Full client load & projects",
      gradient: "from-blue-400 to-cyan-400"
    },
    {
      name: "Mark Murphy",
      program: "Clinical Psychology Student",
      quote: "I could not be more grateful to Alli for the opportunity they have given me. It is a fabulous practicum placement because of the wide variety of clients and cases. My colleagues are second to none and the staff could not be more welcoming and encouraging. I would choose Alli for my practicum again in a heartbeat.",
      image: "MM",
      highlight: "Would choose again",
      gradient: "from-green-400 to-emerald-400"
    },
    {
      name: "Jessie MacAlpine Shearer",
      program: "Counselling Psychology Student",
      quote: "My practicum at Alli allowed me to develop my skills and build my confidence as a therapist. I had the opportunity to conduct group, couples, and individual sessions with the support and feedback from my supervisor and peers. This experience makes me confident that I could establish a private practice or work within any mental health organization.",
      image: "JM",
      highlight: "Ready for any setting",
      gradient: "from-orange-400 to-amber-400"
    }
  ];


  const faqs = [
    {
      question: "What makes Alli's practicum different from traditional placements?",
      answer: "Unlike traditional placements, Alli offers: complete flexibility to work from anywhere, a diverse caseload from day one, integrated technology training, paid supervision (saving you $5000+), and a direct pathway to employment. Plus, you're part of a mission to make therapy accessible to everyone.",
      icon: Sparkles
    },
    {
      question: "How much supervision will I receive?",
      answer: "You'll receive comprehensive supervision including: 90 minutes of weekly dyad supervision (paired with another student), monthly group supervision, unlimited email support, monthly workshops on specialized topics, and access to our resource library. All supervision is provided at no cost (typically worth $200-250/hour elsewhere).",
      icon: MessageCircle
    },
    {
      question: "Can international students apply?",
      answer: "Yes! International students can apply as long as they're enrolled in a Canadian university program and have the appropriate study/work permits. We'll work with your school's international office to ensure all requirements are met.",
      icon: MapPin
    },
    {
      question: "What's the time commitment?",
      answer: "Minimum 25 hours per week with at least 10 client sessions. This typically includes: 10-12 hours of client sessions, 90 minutes of weekly supervision, documentation and prep time, and participation in team meetings and trainings. This ensures you get substantial clinical experience during your practicum.",
      icon: Clock
    },
    {
      question: "Do you provide malpractice insurance?",
      answer: "Students must have their own professional liability insurance (most schools provide this). If your school doesn't provide coverage, we can guide you to affordable options (typically $200-300/year for students).",
      icon: Shield
    },
    {
      question: "What happens after graduation?",
      answer: "Upon completing your practicum, you'll graduate with 200+ clinical hours, valuable experience with diverse clients, and strong virtual therapy skills. You'll join our alumni network where you can stay connected with peers, access ongoing professional development opportunities, and continue to be part of the Alli community.",
      icon: Rocket
    }
  ];

  const whyAlliReasons = [
    { text: "No unpaid supervision fees (save $5000+)", icon: DollarSign },
    { text: "Work from anywhere in Canada", icon: MapPin },
    { text: "Choose your own hours", icon: Calendar },
    { text: "Modern, paperless platform", icon: Laptop },
    { text: "Diverse, engaged client base", icon: Users },
    { text: "Ongoing professional development & training", icon: BookOpen },
    { text: "Supportive peer community & mentorship", icon: Heart }
  ];

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Hero Section - Redesigned */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-4 overflow-hidden">
        {/* Light gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-violet-50">
          <div className="absolute inset-0 bg-gradient-to-b from-purple-100/30 to-transparent" />
        </div>

        {/* Very subtle floating elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-100/20 rounded-full filter blur-3xl" />
          <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-violet-100/20 rounded-full filter blur-3xl" />
          <div className="absolute bottom-1/2 left-1/2 w-96 h-96 bg-purple-100/20 rounded-full filter blur-3xl" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-purple-50/80 backdrop-blur-md px-6 py-3 rounded-full mb-8">
            <GraduationCap className="w-5 h-5 text-purple-400" />
            <span className="text-purple-700 font-medium">Applications Open</span>
            <GraduationCap className="w-5 h-5 text-purple-400" />
          </div>

          {/* Main headline */}
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
            Where Intern Therapists
            <span className="block bg-gradient-to-r from-purple-600 to-violet-500 bg-clip-text text-transparent">
              Grow.
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto mb-10 leading-relaxed">
            Built for intern therapists who want real world experience with meaningful support.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              size="lg"
              className="bg-purple-600 text-white hover:bg-purple-700 px-10 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
              onClick={() => setIsQuizOpen(true)}
            >
              Find My Path
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {impactStats.map((stat, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-md rounded-2xl p-6 transform hover:scale-105 transition-all shadow-lg hover:shadow-xl">
                <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                  <stat.icon className="w-6 h-6 text-gray-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.number}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-purple-400" />
        </div>
      </section>

      {/* Trusted By Schools Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
              Trusted by Interns Across Training Programs
            </h3>
            <p className="text-gray-500 text-sm">
              We work with practicum students from leading counselling and psychology programs
            </p>
          </div>

          {/* School Logos Marquee */}
          <div className="relative overflow-hidden">
            <div className="flex animate-marquee">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((num, idx) => (
                <div
                  key={idx}
                  className="flex-shrink-0 mx-8 md:mx-12 grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
                >
                  <img
                    src={`/school_${num}.png`}
                    alt={`Partner school ${num}`}
                    className="h-20 md:h-28 w-auto object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Problem - Reality Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-rose-50 px-4 py-2 rounded-full mb-6">
              <div className="w-2 h-2 bg-rose-400 rounded-full"></div>
              <span className="text-rose-700 font-medium text-sm">The Reality</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900">
              Most practicum placements<br />
              <span className="text-gray-400">happen in small clinics.</span>
            </h2>
          </div>

          {/* Reality Cards */}
          <div className="grid md:grid-cols-2 gap-5 mb-16">
            {[
              {
                icon: Users,
                title: "You're often the only intern.",
                description: "There's no cohort, no peers going through it with you—just you and your caseload.",
                color: "from-blue-500 to-indigo-600",
                bgColor: "bg-blue-50",
                borderColor: "border-blue-100"
              },
              {
                icon: Clock,
                title: "Your supervisor is busy.",
                description: "They're running a practice, seeing their own clients, and squeezing in your hour when they can.",
                color: "from-amber-500 to-orange-600",
                bgColor: "bg-amber-50",
                borderColor: "border-amber-100"
              },
              {
                icon: Zap,
                title: "You're thrown into sessions.",
                description: "Real clients, real problems—but without the real-world, hands-on training to back it up.",
                color: "from-purple-500 to-violet-600",
                bgColor: "bg-purple-50",
                borderColor: "border-purple-100"
              },
              {
                icon: MessageCircle,
                title: "Nobody talks about the in-between.",
                description: "The self-doubt after a hard session. The questions you don't know who to ask.",
                color: "from-rose-500 to-pink-600",
                bgColor: "bg-rose-50",
                borderColor: "border-rose-100"
              }
            ].map((item, index) => (
              <div
                key={index}
                className={`${item.bgColor} ${item.borderColor} border rounded-2xl p-6 md:p-8 hover:shadow-lg transition-all duration-300 group`}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom Statement - The Solution */}
          <div className="bg-gradient-to-br from-purple-100 to-primary-100 rounded-3xl p-8 md:p-12 border border-purple-200">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xl md:text-2xl text-gray-600 mb-2">
                Placement gives you hours.
              </p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                But hours alone don't make you a confident therapist.
              </p>

              <div className="w-16 h-1 bg-purple-300 mx-auto mb-6 rounded-full"></div>

              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                That's why we built <span className="font-semibold text-purple-700">Between Sessions</span>—a support system designed specifically for intern therapists navigating their practicum.
              </p>

              <div className="bg-white/60 rounded-2xl p-6 md:p-8 backdrop-blur-sm">
                <p className="text-gray-800 text-base md:text-lg leading-relaxed mb-4">
                  <strong>Already have a placement?</strong> Join our community for peer support, group supervision, and the resources you need to thrive—wherever you're placed.
                </p>
                <p className="text-gray-800 text-base md:text-lg leading-relaxed">
                  <strong>Looking for a placement?</strong> Do your practicum with us at Alli Therapy and get real clients, real experience, plus all the support built in.
                </p>
              </div>

              <p className="text-purple-700 font-medium mt-6 text-base md:text-lg">
                Either way, you get the mentorship, community, and guidance that most placements leave out.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Is Alli the Right Fit Section */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-primary-50 px-4 py-2 rounded-full mb-6">
              <HelpCircle className="w-4 h-4 text-primary-500" />
              <span className="text-primary-700 font-medium text-sm">Exploring Alli Placement?</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Is Practicum with Alli Therapy<br />
              the right fit for you?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Alli is a pay-what-you-can, 100% virtual clinic focused on affordable long-term therapy across Canada. We are a tech-forward, real-world training clinic built for clinicians who are comfortable working independently in a modern care environment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Great Fit Card */}
            <Card className="p-8 relative overflow-hidden border-2 border-green-200 bg-green-50/30">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full opacity-10 -mr-16 -mt-16" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <h3 className="text-2xl font-bold text-gray-900">Likely a great fit if you...</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "Are passionate about increasing access to therapy",
                    "Have some real-world clinical experience",
                    "Believe in long-term therapy",
                    "Are open to practicing with a wide range of modalities",
                    "Want a varied caseload",
                    "Are comfortable with marketing and self-promotion",
                    "Feel ready to build and keep a caseload",
                    "Enjoy working in a fully virtual, tech-based clinic",
                    "Can commit 25+ hours per week"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>

            {/* May Not Be Right Fit Card */}
            <Card className="p-8 relative overflow-hidden border-2 border-amber-200 bg-amber-50/30">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full opacity-10 -mr-16 -mt-16" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <AlertCircle className="w-8 h-8 text-amber-500" />
                  <h3 className="text-2xl font-bold text-gray-900">May not be the right fit if you...</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "Prefer a slower-paced environment",
                    "Have limited time available",
                    "Cannot do daytime EST hours for supervision",
                    "Are passionate about supporting a niche or specific population",
                    "Prefer in-person work over 100% virtual",
                    "Want to practice in a specific modality only",
                    "Do not yet have clinical experience",
                    "Prefer short-term therapy",
                    "Are not comfortable using and learning new technology"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                        <div className="w-2 h-2 bg-amber-500 rounded-full" />
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* What's Included - Feature Comparison with Pricing */}
      <section className="py-24 px-4 bg-gradient-to-b from-slate-50 via-white to-violet-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-violet-100/60 px-4 py-2 rounded-full mb-6">
              <Star className="w-4 h-4 text-violet-500" />
              <span className="text-violet-700 font-medium text-sm">Choose Your Path</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Everything You Get as a Member
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Do your practicum with us, or join from wherever you're placed—we're here to support you either way
            </p>
          </div>

          {/* Pricing Cards with Features */}
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 mb-20">
            {/* Core Card */}
            <div className="group bg-violet-50/30 rounded-3xl p-6 md:p-8 border border-violet-200/50 hover:border-violet-300/60 hover:shadow-lg transition-all duration-300 flex flex-col">
              <div className="mb-6">
                <div className="inline-flex items-center gap-1.5 bg-violet-100/60 text-violet-700/80 text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                  <MapPin className="w-3 h-3" />
                  Already have a placement?
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Core</h3>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-lg text-gray-400 line-through">$75</span>
                  <span className="text-4xl font-bold text-gray-900">$45</span>
                  <span className="text-gray-500">/month</span>
                  <span className="text-violet-600/70 text-sm font-medium">Student rate</span>
                </div>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Go through your practicum at another site while being part of a supportive therapist community.
              </p>

              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">What's included</p>
              <ul className="space-y-3 mb-6 flex-grow">
                {[
                  { icon: MessageCircle, text: "Private Slack community", id: "slack-community" },
                  { icon: BookOpen, text: "Full resource library", id: "resource-library" },
                  { icon: Clock, text: "Hours tracking tools", id: "hours-tracking" },
                  { icon: Lightbulb, text: "Monthly knowledge share", id: "knowledge-share" },
                  { icon: Rocket, text: "Monthly LevelUp Labs", id: "levelup-labs" },
                  { icon: Percent, text: "Member discounts", id: "member-discounts" },
                ].map((item, idx) => (
                  <li key={idx}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="flex items-center gap-2.5 group/item cursor-pointer"
                    >
                      <div className="w-7 h-7 rounded-lg bg-violet-100/60 flex items-center justify-center flex-shrink-0 group-hover/item:bg-violet-200/60 transition-colors">
                        <item.icon className="w-3.5 h-3.5 text-violet-600/80" />
                      </div>
                      <span className="text-gray-700 text-sm group-hover/item:text-violet-600 transition-colors">{item.text}</span>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="bg-violet-50/60 rounded-xl p-4 mb-6">
                <p className="text-xs font-semibold text-violet-600/70 uppercase tracking-wide mb-1">Best if</p>
                <p className="text-sm text-violet-700/80">
                  You want community, shared tools, and a place to grow alongside other therapists.
                </p>
              </div>

              <Button
                className="w-full bg-violet-500/80 hover:bg-violet-600/80 text-white py-5 text-base font-semibold"
                onClick={() => window.location.href = '/between-sessions/join?tier=core&student=true'}
              >
                Sign Up
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Community Card */}
            <div className="group relative bg-gradient-to-b from-rose-50 to-white rounded-3xl p-6 md:p-8 border-2 border-rose-200/60 hover:border-rose-300 hover:shadow-xl transition-all duration-300 flex flex-col">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-rose-400 to-pink-500 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg uppercase tracking-wide">
                  Most Popular
                </span>
              </div>

              <div className="mb-6 pt-2">
                <div className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-700 text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                  <MapPin className="w-3 h-3" />
                  Already have a placement?
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Community</h3>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="text-lg text-gray-400 line-through">$125</span>
                  <span className="text-4xl font-bold text-gray-900">$95</span>
                  <span className="text-gray-500">/month</span>
                  <span className="text-rose-600 text-sm font-medium">Student/alumni rate</span>
                </div>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Everything in Core plus structured supervision and peer support.
              </p>

              <div className="bg-violet-100/50 border border-violet-200 rounded-xl p-3 mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-violet-600 flex-shrink-0" />
                  <span className="text-violet-800 font-medium text-sm">Everything in Core</span>
                </div>
              </div>

              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Plus</p>
              <ul className="space-y-3 mb-6 flex-grow">
                {[
                  { icon: Users, text: "Monthly group supervision (90 min, up to 8)", id: "group-supervision" },
                  { icon: Heart, text: "Monthly peer group", id: "peer-group" },
                  { icon: Headphones, text: "À la carte supervision access", id: "alacarte-supervision" },
                ].map((item, idx) => (
                  <li key={idx}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="flex items-center gap-2.5 group/item cursor-pointer"
                    >
                      <div className="w-7 h-7 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0 group-hover/item:bg-rose-200 transition-colors">
                        <item.icon className="w-3.5 h-3.5 text-rose-600" />
                      </div>
                      <span className="text-gray-700 text-sm group-hover/item:text-rose-600 transition-colors">{item.text}</span>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="bg-rose-50 rounded-xl p-4 mb-6">
                <p className="text-xs font-semibold text-rose-700 uppercase tracking-wide mb-1">Best if</p>
                <p className="text-sm text-rose-800">
                  You want structured support with group supervision and a peer cohort.
                </p>
              </div>

              <Button
                className="w-full bg-rose-500 hover:bg-rose-600 text-white py-5 text-base font-semibold"
                onClick={() => window.location.href = '/between-sessions/join?tier=community&student=true'}
              >
                Sign Up
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Placement Card */}
            <div className="group bg-blue-50/30 rounded-3xl p-6 md:p-8 border border-blue-200/50 hover:border-blue-300/60 hover:shadow-lg transition-all duration-300 flex flex-col">
              <div className="mb-6">
                <div className="inline-flex items-center gap-1.5 bg-blue-100/60 text-blue-700/80 text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                  <Briefcase className="w-3 h-3" />
                  Need a placement site?
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Alli Placement</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-blue-600/80">Free</span>
                  <span className="text-sm text-gray-500">with placement</span>
                </div>
              </div>

              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Complete your practicum with real clients and built-in supervision.
              </p>

              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Placement includes</p>
              <ul className="space-y-3 mb-4">
                {[
                  { icon: Brain, text: "Real client work (15-25 clients)", id: "client-work" },
                  { icon: MessageCircle, text: "Weekly supervision", id: "dyad-supervision" },
                ].map((item, idx) => (
                  <li key={idx}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="flex items-center gap-2.5 group/item cursor-pointer"
                    >
                      <div className="w-7 h-7 rounded-lg bg-blue-100/60 flex items-center justify-center flex-shrink-0 group-hover/item:bg-blue-200/60 transition-colors">
                        <item.icon className="w-3.5 h-3.5 text-blue-600/80" />
                      </div>
                      <span className="text-gray-700 text-sm group-hover/item:text-blue-600 transition-colors">{item.text}</span>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="bg-rose-50/50 border border-rose-200/60 rounded-xl p-3 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                  <span className="text-rose-600 font-medium text-sm">Everything in Community</span>
                </div>
                <p className="text-rose-400 text-xs ml-6">Group supervision, peer group, resources & more</p>
              </div>

              <div className="flex-grow"></div>

              <div className="bg-blue-50/60 rounded-xl p-4 mb-6">
                <p className="text-xs font-semibold text-blue-600/70 uppercase tracking-wide mb-1">Best if</p>
                <p className="text-sm text-blue-700/80">
                  You want a full placement with real clients and comprehensive supervision.
                </p>
              </div>

              <Button
                className="w-full bg-blue-500/80 hover:bg-blue-600/80 text-white py-5 text-base font-semibold"
                onClick={() => setIsQuizOpen(true)}
              >
                Apply to Placement
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Feature Deep Dives */}
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Feature Details</h3>
              <p className="text-gray-600">Click any feature above to learn more</p>
            </div>

            <div className="space-y-3">
              {/* Core Features */}
              <div id="slack-community" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-violet-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Private Slack Community</h4>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Connect daily with fellow practicum students. Ask questions, share wins, process tough sessions, and build lasting friendships.
                    </p>
                  </div>
                </div>
              </div>

              <div id="resource-library" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-sky-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-400 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Full Resource Library</h4>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Clinical tools, templates, treatment planning guides, intervention cheat sheets, and client-ready resources to share in sessions.
                    </p>
                  </div>
                </div>
              </div>

              <div id="hours-tracking" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-emerald-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-400 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Hours Tracking Tools</h4>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Track DCC, indirect client hours, supervision, and professional development hours in one place. Export easily to stay compliant with your program.
                    </p>
                  </div>
                </div>
              </div>

              <div id="knowledge-share" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-amber-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Monthly Knowledge Share Sessions</h4>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Live sessions with expert clinicians on specialized topics. Interactive Q&A, recordings available, counts toward PD hours.
                    </p>
                  </div>
                </div>
              </div>

              <div id="member-discounts" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-pink-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-rose-400 flex items-center justify-center flex-shrink-0">
                    <Percent className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Member Discounts</h4>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Exclusive discounts on professional liability insurance, continuing education courses, and so much more.
                    </p>
                  </div>
                </div>
              </div>

              {/* LevelUp Labs CTA */}
              <div
                id="levelup-labs-link"
                className="scroll-mt-24 bg-gradient-to-r from-violet-50 to-fuchsia-50 rounded-xl p-5 border-2 border-violet-200/60 hover:border-violet-300 hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => {
                  document.getElementById('levelup-labs')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-fuchsia-400 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Rocket className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Monthly LevelUp Labs</h4>
                      <span className="px-2 py-0.5 bg-fuchsia-100 text-fuchsia-700 text-[10px] font-bold rounded-full">8-MONTH CURRICULUM</span>
                      <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full">ALL TIERS</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Our signature curriculum takes you from nervous beginner to confident, career-ready therapist.
                    </p>
                    <div className="flex items-center gap-2 text-violet-600 font-medium text-sm mt-2 group-hover:gap-3 transition-all">
                      <span>See full curriculum below</span>
                      <ChevronDown className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Community Features */}
              <div id="group-supervision" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-rose-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-400 to-pink-400 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Monthly Group Supervision</h4>
                      <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-bold rounded-full">COMMUNITY</span>
                      <span className="px-2 py-0.5 bg-blue-100/60 text-blue-600/80 text-[10px] font-bold rounded-full">PLACEMENT</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      90-minute sessions with up to 8 therapists, led by experienced supervisors. Bring cases, get feedback, learn from peers.
                    </p>
                  </div>
                </div>
              </div>

              <div id="peer-group" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-teal-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Monthly Peer Group</h4>
                      <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-bold rounded-full">COMMUNITY</span>
                      <span className="px-2 py-0.5 bg-blue-100/60 text-blue-600/80 text-[10px] font-bold rounded-full">PLACEMENT</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Connect with a small group of peers each month to share experiences, grow together, and learn from each other's clinical journeys.
                    </p>
                  </div>
                </div>
              </div>

              <div id="alacarte-supervision" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-400 to-violet-400 flex items-center justify-center flex-shrink-0">
                    <Headphones className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">À la Carte Supervision Access</h4>
                      <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-[10px] font-bold rounded-full">COMMUNITY</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Book individual supervision at member rates when you need extra support beyond your regular sessions.
                    </p>
                  </div>
                </div>
              </div>

              {/* Placement Features */}
              <div id="client-work" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-sky-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-400 flex items-center justify-center flex-shrink-0">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Real Client Work</h4>
                      <span className="px-2 py-0.5 bg-blue-100/60 text-blue-600/80 text-[10px] font-bold rounded-full">PLACEMENT</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Work with 15-25 diverse clients through Alli's platform. Earn all required clinical hours while making real impact.
                    </p>
                  </div>
                </div>
              </div>

              <div id="dyad-supervision" className="scroll-mt-24 bg-white rounded-xl p-5 border border-gray-100 hover:border-violet-200 hover:shadow-md transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-purple-400 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">Weekly Supervision</h4>
                      <span className="px-2 py-0.5 bg-blue-100/60 text-blue-600/80 text-[10px] font-bold rounded-full">PLACEMENT</span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                      Dedicated weekly supervision with your clinical supervisor. Personalized feedback and case consultation included.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monthly LevelUp Labs Section */}
      <section id="levelup-labs" className="py-24 px-4 bg-gradient-to-b from-purple-50 via-white to-white overflow-hidden scroll-mt-16">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-pink-100 px-5 py-2.5 rounded-full mb-6 shadow-sm">
              <Zap className="w-4 h-4 text-purple-600" />
              <span className="text-purple-700 font-semibold text-sm">Included in All Programs</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Monthly LevelUp Labs
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Each month builds on the last, taking you from nervous beginner to confident, career-ready therapist
            </p>
          </div>

          {/* Interactive Lab Viewer */}
          {(() => {
            const labs = [
              {
                month: 1,
                phase: "Foundation",
                title: "Foundations, Client Nerves & Informed Consent",
                description: "Start strong by learning the core skills that help you feel more confident in your first sessions. We will focus on calming those early therapist nerves, building solid habits, and making sure you know how to work safely and clearly with clients from day one.",
                highlights: ["Managing first-session anxiety", "Building solid clinical habits", "Informed consent best practices", "Setting up for success"],
                icon: Shield,
                gradient: "from-blue-500 to-indigo-600",
                lightBg: "bg-blue-50",
                accentColor: "blue"
              },
              {
                month: 2,
                phase: "Foundation",
                title: "Record Keeping & Session Flow",
                description: "Learn how to write notes that actually make sense and support your clinical work. You will also build skills to move smoothly from an intake to follow up sessions so your work feels more organized and less stressful.",
                highlights: ["Clinical documentation that works", "Intake to follow-up transitions", "Staying organized", "Time management in sessions"],
                icon: FileText,
                gradient: "from-violet-500 to-purple-600",
                lightBg: "bg-violet-50",
                accentColor: "violet"
              },
              {
                month: 3,
                phase: "Foundation",
                title: "Client Engagement Skills",
                description: "Build the skills that help clients feel safe, understood, and excited to keep coming back. You will learn how to create strong connections, handle awkward moments, and keep therapy feeling meaningful and helpful.",
                highlights: ["Creating strong connections", "Handling awkward moments", "Keeping clients engaged", "Building therapeutic rapport"],
                icon: Heart,
                gradient: "from-pink-500 to-rose-600",
                lightBg: "bg-pink-50",
                accentColor: "pink"
              },
              {
                month: 4,
                phase: "Foundation",
                title: "Navigating Client Requests & Safety",
                description: "Feel more confident handling tricky questions, big asks, and tough moments in session. You will learn how to trust your clinical thinking and make thoughtful choices instead of feeling stuck or unsure what to say.",
                highlights: ["Handling difficult requests", "Trusting your clinical judgment", "Safety planning", "Setting therapeutic boundaries"],
                icon: Target,
                gradient: "from-amber-500 to-orange-600",
                lightBg: "bg-amber-50",
                accentColor: "amber"
              },
              {
                month: 5,
                phase: "Growth",
                title: "Next Level Skills, Your Niche & Being You",
                description: "Start figuring out what kind of therapist you want to be and who you work best with. You will learn how to talk about your work in a real and honest way and how to grow your caseload without feeling salesy or fake.",
                highlights: ["Finding your therapeutic niche", "Authentic self-presentation", "Growing your caseload", "Marketing without feeling fake"],
                icon: Sparkles,
                gradient: "from-teal-500 to-emerald-600",
                lightBg: "bg-teal-50",
                accentColor: "teal"
              },
              {
                month: 6,
                phase: "Growth",
                title: "Cultivating Affirmative Practice",
                description: "Grow your confidence working with more complex stories and identities. You will learn how to hold your values in the room, support clients in affirming ways, and still take care of your own energy.",
                highlights: ["Working with diverse identities", "Values-aligned practice", "Affirmative approaches", "Therapist self-care"],
                icon: HandHeart,
                gradient: "from-purple-500 to-violet-600",
                lightBg: "bg-purple-50",
                accentColor: "purple"
              },
              {
                month: 7,
                phase: "Growth",
                title: "Professional Identity & Your Path Forward",
                description: "Get clearer on your style, your strengths, and where you want to focus next. You will learn when to keep going with a client, when to ask for help, and when a referral is the best care.",
                highlights: ["Defining your therapeutic style", "Knowing when to refer", "Seeking consultation", "Long-term career vision"],
                icon: Briefcase,
                gradient: "from-indigo-500 to-blue-600",
                lightBg: "bg-indigo-50",
                accentColor: "indigo"
              },
              {
                month: 8,
                phase: "Career Ready",
                title: "Career Ready & What's Next!",
                description: "Look back at how far you have come and get ready for your next step as a therapist. You will leave feeling more confident, more grounded, and way more ready for real world practice.",
                highlights: ["Celebrating your growth", "Next steps planning", "Career readiness", "Joining the alumni network"],
                icon: Award,
                gradient: "from-orange-500 to-red-500",
                lightBg: "bg-orange-50",
                accentColor: "orange"
              }
            ];

            const currentLab = labs[activeLab];
            if (!currentLab) return null;
            const IconComponent = currentLab.icon;

            return (
              <div className="relative">
                {/* Month Selector - Pill Navigation */}
                <div className="flex justify-center mb-8">
                  <div className="inline-flex items-center bg-white rounded-full p-1.5 shadow-lg border border-gray-100">
                    {labs.map((lab, index) => (
                      <button
                        key={lab.month}
                        onClick={() => setActiveLab(index)}
                        className={`relative w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                          activeLab === index
                            ? `bg-gradient-to-br ${lab.gradient} text-white shadow-lg scale-110`
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {lab.month}
                        {activeLab === index && (
                          <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-medium text-gray-500 whitespace-nowrap">
                            Month {lab.month}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Phase Indicator */}
                <div className="text-center mb-6 mt-8">
                  <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium ${
                    currentLab.phase === 'Foundation'
                      ? 'bg-purple-100 text-purple-700'
                      : currentLab.phase === 'Growth'
                      ? 'bg-pink-100 text-pink-700'
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {currentLab.phase === 'Foundation' && 'Phase 1: Building Your Foundation'}
                    {currentLab.phase === 'Growth' && 'Phase 2: Growth & Mastery'}
                    {currentLab.phase === 'Career Ready' && 'Phase 3: Career Readiness'}
                  </span>
                </div>

                {/* Main Content Card */}
                <div className={`relative ${currentLab.lightBg} rounded-3xl p-8 md:p-12 shadow-xl border border-white/50 transition-all duration-500`}>
                  {/* Decorative background gradient */}
                  <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-br ${currentLab.gradient} opacity-5 rounded-full blur-3xl -mr-32 -mt-32`} />

                  <div className="relative z-10">
                    {/* Header with Icon */}
                    <div className="flex flex-col md:flex-row md:items-start gap-6 mb-8">
                      <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${currentLab.gradient} flex items-center justify-center shadow-xl flex-shrink-0`}>
                        <IconComponent className="w-10 h-10 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                          {currentLab.title}
                        </h3>
                        <p className="text-gray-600 text-lg leading-relaxed">
                          {currentLab.description}
                        </p>
                      </div>
                    </div>

                    {/* What You'll Learn */}
                    <div className="bg-white/60 rounded-2xl p-6 backdrop-blur-sm">
                      <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                        What You'll Learn
                      </h4>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {currentLab.highlights.map((highlight, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${currentLab.gradient} flex items-center justify-center flex-shrink-0`}>
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-gray-700 font-medium">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Navigation Arrows */}
                <div className="flex justify-between items-center mt-8">
                  <button
                    onClick={() => setActiveLab(prev => prev > 0 ? prev - 1 : labs.length - 1)}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-purple-600 transition-colors group"
                  >
                    <ArrowRight className="w-5 h-5 rotate-180 group-hover:-translate-x-1 transition-transform" />
                    <span className="font-medium">Previous</span>
                  </button>

                  {/* Progress dots for mobile */}
                  <div className="flex gap-1.5 md:hidden">
                    {labs.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveLab(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          activeLab === index
                            ? 'w-6 bg-purple-600'
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={() => setActiveLab(prev => prev < labs.length - 1 ? prev + 1 : 0)}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-purple-600 transition-colors group"
                  >
                    <span className="font-medium">Next</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>

                {/* Progress indicator */}
                <div className="mt-6">
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${currentLab.gradient} transition-all duration-500 rounded-full`}
                      style={{ width: `${((activeLab + 1) / labs.length) * 100}%` }}
                    />
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-3">
                    {activeLab + 1} of {labs.length} months
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Bottom Info */}
          <div className="mt-16 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-gradient-to-r from-purple-100 via-pink-50 to-purple-100 rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-sm text-gray-500">Available in Core, Community & Placement</p>
                  <p className="font-bold text-gray-900">Graduate confident & career-ready</p>
                </div>
              </div>
              <Button
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 font-semibold shadow-lg hover:shadow-xl transition-all"
                onClick={() => setIsQuizOpen(true)}
              >
                Get Started
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Cohort Calendar Section */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">Cohort Calendar</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
              Three Intakes Per Year
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join a cohort that fits your schedule. Each cohort runs for 8 months.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {/* Winter */}
            <div className={`relative rounded-2xl p-6 border transition-all ${nextCohort.season === 'winter' ? 'bg-sky-50 border-sky-200 shadow-lg ring-2 ring-sky-300' : 'bg-gray-50 border-gray-100 hover:border-gray-200'}`}>
              {nextCohort.season === 'winter' && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 flex gap-2">
                  <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md animate-pulse">
                    Accepting Applications
                  </span>
                </div>
              )}
              <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-4 mt-2">
                <span className="text-xl">❄️</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Winter Cohort</h3>
              <p className="text-sky-600 font-medium text-sm mb-2">Starts January 1</p>
              <p className="text-gray-500 text-sm">Runs January through August. Applications close December 1.</p>
            </div>

            {/* Spring */}
            <div className={`relative rounded-2xl p-6 border transition-all ${nextCohort.season === 'spring' ? 'bg-rose-50 border-rose-200 shadow-lg ring-2 ring-rose-300' : 'bg-gray-50 border-gray-100 hover:border-gray-200'}`}>
              {nextCohort.season === 'spring' && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 flex gap-2">
                  <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md animate-pulse">
                    Accepting Applications
                  </span>
                </div>
              )}
              <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center mb-4 mt-2">
                <span className="text-xl">🌸</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Spring Cohort</h3>
              <p className="text-rose-500 font-medium text-sm mb-2">Starts May 1</p>
              <p className="text-gray-500 text-sm">Runs May through December. Applications close April 1.</p>
            </div>

            {/* Fall */}
            <div className={`relative rounded-2xl p-6 border transition-all ${nextCohort.season === 'fall' ? 'bg-amber-50 border-amber-200 shadow-lg ring-2 ring-amber-300' : 'bg-gray-50 border-gray-100 hover:border-gray-200'}`}>
              {nextCohort.season === 'fall' && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 flex gap-2">
                  <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md animate-pulse">
                    Accepting Applications
                  </span>
                </div>
              )}
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4 mt-2">
                <span className="text-xl">🍂</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Fall Cohort</h3>
              <p className="text-amber-600 font-medium text-sm mb-2">Starts September 1</p>
              <p className="text-gray-500 text-sm">Runs September through April. Applications close August 1.</p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-gray-600 mb-4">
              Now accepting applications for <span className="font-semibold text-gray-900">{nextCohort.name}</span> starting {nextCohort.displayDate}
            </p>
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-primary-600 text-white hover:shadow-xl"
              onClick={() => setIsQuizOpen(true)}
            >
              Find My Path <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Program Timeline Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">Program Timeline</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Your Path Forward
            </h2>
          </div>

          <div className="space-y-0">
            {/* Step 1: Join */}
            <div className="relative">
              <div className="flex gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    1
                  </div>
                  <div className="w-0.5 h-full bg-gradient-to-b from-purple-300 to-rose-300 mt-4" />
                </div>
                <div className="flex-1 pb-12">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Join Between Sessions</h3>
                  <p className="text-purple-600 font-medium text-sm mb-3">Choose Your Starting Point</p>
                  <p className="text-gray-600 leading-relaxed">
                    Choose one of three ways to begin. Apply for practicum placement, join Community, or start with Core. Start in the path that fits where you are right now and your placement plans.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2: Cohort Starts */}
            <div className="relative">
              <div className="flex gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    2
                  </div>
                  <div className="w-0.5 h-full bg-gradient-to-b from-rose-300 to-blue-300 mt-4" />
                </div>
                <div className="flex-1 pb-12">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Cohort Starts</h3>
                  <p className="text-rose-600 font-medium text-sm mb-3">Cohort-Based Onboarding</p>
                  <p className="text-gray-600 leading-relaxed">
                    New cohorts begin three times per year in January, May, and September. You'll be matched with other interns at the same stage so your learning, support, and community grow together throughout placement.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3a & 3b: Placement paths */}
            <div className="relative">
              <div className="flex gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    3
                  </div>
                  <div className="w-0.5 h-full bg-gradient-to-b from-blue-300 to-emerald-300 mt-4" />
                </div>
                <div className="flex-1 pb-12">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Alli Placement */}
                    <div className="bg-blue-50 rounded-2xl p-5 border border-blue-100">
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Alli Placement</span>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">Placement & Supervision Begin Together</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        If you're accepted into practicum at Alli, your placement and your Between Sessions cohort start at the same time. Your supervision, client work, and community are built into one connected experience.
                      </p>
                    </div>
                    {/* External Placement */}
                    <div className="bg-rose-50 rounded-2xl p-5 border border-rose-100">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-rose-600" />
                        <span className="text-xs font-semibold text-rose-600 uppercase tracking-wide">Placement Elsewhere</span>
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">Community & Support During Practicum</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        If you're completing practicum at another site, you can still join Between Sessions in Community or Core. You'll move through placement with cohort support, learning, and optional group supervision alongside other interns.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4: After Placement */}
            <div className="relative">
              <div className="flex gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    4
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">After Placement</h3>
                  <p className="text-emerald-600 font-medium text-sm mb-3">Stay and Keep Growing</p>
                  <p className="text-gray-600 leading-relaxed">
                    When placement ends, your support doesn't. Stay in Between Sessions as you transition into independent practice.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-primary-600 text-white hover:shadow-xl"
              onClick={() => setIsQuizOpen(true)}
            >
              Find My Path <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Success Stories - Redesigned */}
      <section className="py-24 px-4 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">Success Stories</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Hear From Our Alumni
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real stories from students who launched their careers with us
            </p>
          </div>

          <div className="grid lg:grid-cols-4 gap-6 mb-12">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="group">
                <Card className="p-6 h-full hover:shadow-2xl transition-all duration-300 relative overflow-hidden">
                  {/* Gradient accent */}
                  <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${testimonial.gradient}`} />
                  
                  {/* Stars */}
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <blockquote className="text-gray-700 mb-6 italic leading-relaxed">
                    "{testimonial.quote}"
                  </blockquote>
                  
                  {/* Author */}
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${testimonial.gradient} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                      {testimonial.image}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.program}</p>
                      <p className="text-xs font-semibold text-purple-600 mt-1">{testimonial.highlight}</p>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* FAQ - Modern Accordion */}
      <section className="py-24 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-purple-600 font-semibold text-sm uppercase tracking-wider">FAQ</span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
              Your Questions Answered
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-2xl hover:border-purple-300 transition-colors overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-purple-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <faq.icon className="w-6 h-6 text-purple-600" />
                    <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-purple-600 transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {expandedFaq === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-600 leading-relaxed pl-10">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Styles for animations */}
      <style jsx>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .animate-marquee {
          animation: marquee 30s linear infinite;
        }

        .animate-marquee:hover {
          animation-play-state: paused;
        }

        @keyframes blob {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }

        @keyframes fade-in-down {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animate-fade-in-down {
          animation: fade-in-down 0.8s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out;
        }

        .animation-delay-200 {
          animation-delay: 200ms;
        }

        .animation-delay-400 {
          animation-delay: 400ms;
        }

        .animation-delay-600 {
          animation-delay: 600ms;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      {/* Path Quiz Modal */}
      <PathQuizModal
        isOpen={isQuizOpen}
        onClose={() => setIsQuizOpen(false)}
        airtableFormUrl={airtableFormUrl}
      />
    </div>
  );
}